import { fetchJson } from '@aide/api-client'

export type VhoopSourceKind = 'upload' | 'repo' | 'url' | 'evidence_pack' | 'browser_capture' | 'office_capture' | 'mobile_share' | 'unknown'
export type VhoopEvidenceCaptureSource = 'upload' | 'git' | 'browser' | 'office' | 'mobile_share'

export interface VhoopWorkspace {
  workspace_id: string
  tenant_id: string
  owner_user_id: string
  project_id: string
  linked_repo_id: string | null
  display_name: string
  description: string
  status: 'draft' | 'ingesting' | 'ready' | 'assessing' | 'assessed'
  source_kind: VhoopSourceKind
  intake_summary: Record<string, unknown>
  latest_run_id: string | null
  created_at: string
  updated_at: string
}

export interface ManagedSourceSummary {
  repo_id: string
  canonical_path: string
  display_name?: string
  git_repo_name?: string
  http_clone_url?: string
  ssh_clone_url?: string
  forge_workspace_url?: string
}

export interface VhoopWorkspaceCreateRequest {
  display_name: string
  description: string
  source_kind: VhoopSourceKind
  provision_repo?: boolean
  intake_summary?: Record<string, unknown>
}

export interface VhoopWorkspaceSourceUpdateRequest {
  source_kind: VhoopSourceKind
  github_repo_url?: string | null
  deployment_url?: string | null
  notes?: string | null
}

export interface AuthRegisterRequest {
  app?: 'vhoop'
  email: string
  password: string
  full_name: string
  company_name: string
}

export interface VhoopAnalysisFinding {
  title: string
  severity: 'high' | 'medium' | 'low'
  summary: string
  evidence: VhoopFindingEvidence[]
}

export interface VhoopReadinessPillar {
  slug: string
  title: string
  score: number
  raw_score?: number
  summary: string
  status: 'fragile' | 'emerging' | 'steady'
}

export interface VhoopUpgradePath {
  tier_id: 'launch' | 'operate' | 'partner'
  name: string
  tagline: string
  recommended: boolean
  outcome_summary: string
  deliverables: string[]
  cta_label: string
}

export interface VhoopAnalysisProgressEvent {
  event_type: string
  label: string
  detail: string
  created_at: string
}

export interface VhoopFindingEvidence {
  kind:
    | 'repo_file'
    | 'data_asset'
    | 'tool_output'
    | 'missing_artifact'
    | 'evidence_pack'
    | 'source_snapshot'
    | 'context_bundle'
    | 'browser_capture'
    | 'office_capture'
    | 'mobile_share'
    | 'policy_decision'
  label: string
  path?: string
  asset_id?: string
  repo_id?: string
  canonical_path?: string
  url?: string
  source_snapshot_id?: string
}

export type VhoopPlanId = 'free' | 'launch' | 'operate' | 'partner'
export type VhoopExecutionProfileId = 'manifest_validate' | 'typecheck' | 'lint' | 'test' | 'build' | 'start_probe' | 'ci_simulation'

export interface VhoopEntitlementPlan {
  plan_id: VhoopPlanId
  name: string
  monthly_vm_runs: number
  max_vm_minutes_per_run: number
  monthly_llm_calls: number
  max_llm_tier: string
  allowed_execution_profiles: VhoopExecutionProfileId[]
  recurring_checks_enabled: boolean
}

export interface VhoopUsageSnapshot {
  plan_id: VhoopPlanId
  vm_runs_used_this_month: number
  monthly_vm_runs: number
  llm_calls_used_this_month: number
  monthly_llm_calls: number
  remaining_deep_reports: number
  recurring_checks_enabled: boolean
}

export interface VhoopScanResult {
  check_id: string
  category: string
  status: 'pass' | 'warn' | 'fail' | 'locked' | 'not_found' | 'not_applicable'
  severity: 'high' | 'medium' | 'low' | 'info'
  confidence: number
  title: string
  summary: string
  evidence_refs: VhoopFindingEvidence[]
  upgrade_relevance: string
  plan_required?: VhoopPlanId | null
  evidence_strength?: number
  parser_refs?: Record<string, unknown>[]
}

export interface VhoopExecutionResult {
  profile_id: VhoopExecutionProfileId
  profile_name: string
  status: 'succeeded' | 'failed' | 'blocked' | 'skipped' | 'unavailable'
  command: string[]
  exit_code?: number | null
  duration_ms?: number | null
  output_excerpt: string
  artifacts: Record<string, unknown>[]
  evidence_refs: VhoopFindingEvidence[]
  plan_required: VhoopPlanId
  summary: string
}

export interface VhoopImplementationQuality {
  score: number
  raw_score?: number
  plan_cap?: number
  label: string
  summary: string
  evidence_status: 'static-only' | 'execution-backed' | 'evidence-missing' | 'locked'
  signals: Array<{ label: string; status: string; detail: string }>
  locked_checks: Array<{ profile_id: string; title: string; plan_required: VhoopPlanId; reason: string }>
  ai_development_signals: Array<{ label: string; status: string; detail: string }>
}

export interface VhoopScoreBreakdownItem {
  category: string
  weight: number
  earned: number
  max_score: number
  status: 'pass' | 'warn' | 'locked' | 'not_found'
  summary: string
  check_ids: string[]
  check_contributions?: Array<{
    check_id: string
    status: string
    status_points: number
    evidence_strength: number
    check_weight: number
    earned: number
    max_score: number
    source: string
  }>
  evidence_refs: VhoopFindingEvidence[]
}

export interface VhoopEntitlementsResponse {
  current_plan: VhoopEntitlementPlan
  account_plan?: VhoopEntitlementPlan
  plans: VhoopEntitlementPlan[]
  execution_profiles: Array<Record<string, unknown>>
  usage: VhoopUsageSnapshot
  llm_usage: VhoopUsageSnapshot
}

export interface VhoopAnalysisReport {
  run_id: string
  workspace_id: string
  tier: 'free' | 'launch' | 'operate' | 'partner'
  status: 'queued' | 'running' | 'completed' | 'failed'
  score: number | null
  readiness_label: string
  executive_summary: string
  top_risks: string[]
  strengths: string[]
  next_steps: string[]
  pillars: VhoopReadinessPillar[]
  findings: VhoopAnalysisFinding[]
  upgrade_paths: VhoopUpgradePath[]
  created_at: string
  started_at?: string | null
  completed_at?: string | null
  error_message?: string | null
  progress: VhoopAnalysisProgressEvent[]
  trace?: Record<string, unknown> | null
  plan_id: VhoopPlanId
  entitlement_snapshot: Partial<VhoopEntitlementPlan>
  scan_results: VhoopScanResult[]
  execution_results: VhoopExecutionResult[]
  tool_artifacts: Record<string, unknown>[]
  evidence_refs: VhoopFindingEvidence[]
  implementation_quality?: VhoopImplementationQuality | null
  score_breakdown: VhoopScoreBreakdownItem[]
  score_confidence: 'low' | 'medium' | 'high'
  evidence_level: 'no-evidence' | 'metadata-only' | 'static-evidence' | 'execution-backed'
  raw_score: number
  confidence_cap: number
  evidence_confidence_cap?: number
  plan_cap?: number
  critical_penalties: Array<{ id?: string; points: number; title: string; summary: string; evidence_refs?: VhoopFindingEvidence[] }>
  execution_provider: string
  execution_policy: Record<string, unknown>
  llm_usage: Partial<VhoopUsageSnapshot>
  vm_usage: Partial<VhoopUsageSnapshot>
}

export interface VhoopWorkspaceDetail {
  workspace: VhoopWorkspace
  report: VhoopAnalysisReport | null
}

export interface VhoopEvidencePackIntakeRequest {
  capture_source: VhoopEvidenceCaptureSource
  capture_label?: string
  evidence_pack: Record<string, unknown>
}

export interface VhoopPromotionRequest {
  target: 'forge' | 'contextful'
  reason?: string
}

export interface VhoopPromotionResponse {
  promotion_id: string
  workspace_id: string
  target: 'forge' | 'contextful'
  status: 'ready' | 'planned'
  summary: string
  links: Record<string, unknown>
  context: Record<string, unknown>
  created_at: string
}

export interface VhoopIAMControl {
  label: string
  status: 'ready' | 'planned' | 'attention'
  detail: string
}

export interface VhoopIAMStatus {
  app_id: string
  app_display_name: string
  legal_entity_name?: string | null
  realm_name?: string | null
  realm_mode: 'separate_realm' | 'shared_realm'
  tenant_id?: string | null
  tenant_display_name?: string | null
  app_tenant_id?: string | null
  platform_account_id: string
  app_user_id: string
  user_email?: string | null
  user_display_name?: string | null
  provisioning_status: 'ready' | 'claim_backed' | 'degraded'
  keycloak_hidden_by_facade: boolean
  isolation_model: string
  controls: VhoopIAMControl[]
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  return fetchJson<T>(path, init)
}

export async function createWorkspace(payload: VhoopWorkspaceCreateRequest): Promise<VhoopWorkspace> {
  return requestJson<VhoopWorkspace>('/vhoop/workspaces', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function listWorkspaces(): Promise<VhoopWorkspace[]> {
  return requestJson<VhoopWorkspace[]>('/vhoop/workspaces')
}

export async function getIamStatus(): Promise<VhoopIAMStatus> {
  return requestJson<VhoopIAMStatus>('/vhoop/workspaces/iam/status')
}

export async function getWorkspace(workspaceId: string): Promise<VhoopWorkspaceDetail> {
  return requestJson<VhoopWorkspaceDetail>(`/vhoop/workspaces/${encodeURIComponent(workspaceId)}`)
}

export async function registerUser(payload: AuthRegisterRequest) {
  return requestJson('/auth/register', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      app: 'vhoop',
      ...payload,
    }),
  })
}

export async function updateWorkspaceSource(
  workspaceId: string,
  payload: VhoopWorkspaceSourceUpdateRequest,
): Promise<VhoopWorkspaceDetail> {
  return requestJson<VhoopWorkspaceDetail>(`/vhoop/workspaces/${encodeURIComponent(workspaceId)}/intake/source`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function uploadWorkspaceAssets(workspaceId: string, files: File[]): Promise<VhoopWorkspaceDetail> {
  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))

  const response = await fetch(`/api/vhoop/workspaces/${encodeURIComponent(workspaceId)}/intake/upload`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }))
    throw new Error(error.detail || `HTTP ${response.status}`)
  }

  return response.json()
}

export async function submitEvidencePack(
  workspaceId: string,
  payload: VhoopEvidencePackIntakeRequest,
): Promise<VhoopWorkspaceDetail> {
  return requestJson<VhoopWorkspaceDetail>(`/vhoop/workspaces/${encodeURIComponent(workspaceId)}/intake/evidence-pack`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function promoteWorkspace(
  workspaceId: string,
  payload: VhoopPromotionRequest,
): Promise<VhoopPromotionResponse> {
  return requestJson<VhoopPromotionResponse>(`/vhoop/workspaces/${encodeURIComponent(workspaceId)}/promotions`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function runQuickAssessment(workspaceId: string): Promise<VhoopAnalysisReport> {
  return requestJson<VhoopAnalysisReport>(`/vhoop/workspaces/${encodeURIComponent(workspaceId)}/analysis/quick`, {
    method: 'POST',
  })
}

export async function runStaticAssessment(workspaceId: string): Promise<VhoopAnalysisReport> {
  return requestJson<VhoopAnalysisReport>(`/vhoop/workspaces/${encodeURIComponent(workspaceId)}/analysis/static`, {
    method: 'POST',
  })
}

export async function runDeepAssessment(
  workspaceId: string,
  payload: { plan_id: VhoopPlanId; execution_profiles: VhoopExecutionProfileId[]; use_agent?: boolean },
): Promise<VhoopAnalysisReport> {
  return requestJson<VhoopAnalysisReport>(`/vhoop/workspaces/${encodeURIComponent(workspaceId)}/analysis/deep`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function getWorkspaceEntitlements(workspaceId: string): Promise<VhoopEntitlementsResponse> {
  return requestJson<VhoopEntitlementsResponse>(`/vhoop/workspaces/${encodeURIComponent(workspaceId)}/analysis/entitlements`)
}

export const sampleWorkspaceDetail: VhoopWorkspaceDetail = {
  workspace: {
    workspace_id: 'sample-workspace',
    tenant_id: 'sample-tenant',
    owner_user_id: 'sample-user',
    project_id: 'sample-project',
    linked_repo_id: 'sample-repo',
    display_name: 'Odoo Sale',
    description: 'Operational review for the Odoo sales order and quotation module',
    status: 'assessed',
    source_kind: 'upload',
    intake_summary: {
      forge_repo: {
        repo_id: 'sample-repo',
        canonical_path: 'tenant/sample-tenant/odoo-sale',
        git_repo_name: 'odoo-sale',
        http_clone_url: 'http://localhost:8084/git/odoo-sale.git',
        ssh_clone_url: 'ssh://git@localhost:2222/odoo-sale.git',
        forge_workspace_url: 'http://localhost:3002/repositories/sample-repo',
      },
      source_capture: {
        source_kind: 'repo',
        github_repo_url: 'https://github.com/odoo/odoo/tree/17.0/addons/sale',
        deployment_url: '',
        notes: 'Reviewing the sale module as a business-critical ERP workflow for quotations, orders, invoicing handoff, and operational continuity.',
      },
    },
    latest_run_id: 'sample-run',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  report: {
    run_id: 'sample-run',
    workspace_id: 'sample-workspace',
    tier: 'free',
    status: 'completed',
    score: 76,
    readiness_label: 'Promising, but not fully proven',
    executive_summary: 'Odoo Sale has strong static evidence for a mature sales-order workflow: module manifests, tests, CI, and operational notes are visible. The remaining gap is execution proof: Vhoop has not run the test suite, build, or runtime probes in an isolated environment on the free plan.',
    top_risks: [
      'Sales-order and invoicing handoff behavior is not execution-proven in this Vhoop run',
      'Upgrade and rollback controls need explicit ownership before relying on customizations',
      'Operational monitoring for order confirmation, payment, and invoice exceptions is not proven here',
    ],
    strengths: [
      'The application is grounded in managed source evidence for the Odoo Sale module.',
      'Static evidence includes module manifests, sale order tests, CI configuration, and environment documentation.',
      'The snapshot can distinguish mature ERP code structure from missing runtime proof.',
    ],
    next_steps: [
      'Run the Odoo Sale test profile in an isolated paid proof environment to prove sales-order flows.',
      'Document release, migration, and rollback ownership for Odoo module upgrades.',
      'Add monitoring evidence for order confirmation, payment, invoice, and exception queues.',
    ],
    pillars: [
      {
        slug: 'release-safety',
        title: 'Release safety',
        score: 74,
        summary: 'CI and module structure are visible, but release safety still needs execution proof and rollback ownership.',
        status: 'emerging',
      },
      {
        slug: 'security-controls',
        title: 'Security and access',
        score: 68,
        summary: 'Environment documentation is visible, but production access and ERP permission controls still need operational evidence.',
        status: 'emerging',
      },
      {
        slug: 'operations-ownership',
        title: 'Operations ownership',
        score: 71,
        summary: 'Runbook evidence exists, but ownership for incidents, upgrades, and sales-order exception handling needs to be explicit.',
        status: 'emerging',
      },
      {
        slug: 'continuity-observability',
        title: 'Continuity and observability',
        score: 64,
        summary: 'The module has useful static proof, but runtime health, logs, and business continuity signals are not execution-backed.',
        status: 'emerging',
      },
    ],
    findings: [
      {
        title: 'Sales-order behavior is not execution-proven yet',
        severity: 'high',
        summary: 'The static evidence points to mature Odoo Sale flows, but this free snapshot has not run the tests that prove quotations, confirmations, and invoice handoff behavior.',
        evidence: [
          {
            kind: 'repo_file',
            label: 'addons/sale/tests/test_sale_order.py',
            path: 'addons/sale/tests/test_sale_order.py',
            repo_id: 'sample-repo',
            canonical_path: 'tenant/sample-tenant/odoo-sale',
            url: 'http://localhost:3002/repositories/sample-repo/code',
          },
        ],
      },
      {
        title: 'Upgrade and rollback ownership must be explicit',
        severity: 'medium',
        summary: 'Odoo module changes can affect quotations, invoices, payments, and downstream stock workflows, so release ownership needs to be written down.',
        evidence: [
          {
            kind: 'data_asset',
            label: 'odoo-sale.zip:docs/runbook.md',
            asset_id: 'da_sample_1',
          },
        ],
      },
      {
        title: 'Runtime monitoring is still unproven',
        severity: 'medium',
        summary: 'The free snapshot can see operational notes, but it has not started Odoo or inspected health, logs, queues, or exception handling.',
        evidence: [],
      },
    ],
    upgrade_paths: [
      {
        tier_id: 'launch',
        name: 'Launch Blueprint',
        tagline: 'Turn the quick scan into a go or no-go launch packet.',
        recommended: true,
        outcome_summary: 'Close the launch-blocking risks before customers depend on the product.',
        deliverables: [
          'Go or no-go launch memo',
          '30-day remediation sequence for ERP launch risks',
          'Controls checklist for releases, permissions, and rollback',
        ],
        cta_label: 'Request launch blueprint',
      },
      {
        tier_id: 'operate',
        name: 'Operate System',
        tagline: 'Build a repeatable operating spine for a small team.',
        recommended: false,
        outcome_summary: 'Add runbooks, ownership boundaries, and continuity practices for day-two operations.',
        deliverables: [
          'Runbook starter pack',
          'Operational ownership map for sales-order incidents',
          'Observability backlog for order, payment, and invoice exceptions',
        ],
        cta_label: 'Upgrade to operate',
      },
      {
        tier_id: 'partner',
        name: 'Partner Ops',
        tagline: 'Move from guidance into ongoing managed follow-through.',
        recommended: false,
        outcome_summary: 'Use Vhoop as the partner that keeps the repo, controls, and operating model moving in sync.',
        deliverables: [
          'Managed remediation tracking',
          'Recurring operational reviews',
          'Hosting and reliability guidance for ERP operations',
        ],
        cta_label: 'Talk to operator team',
      },
    ],
    created_at: new Date().toISOString(),
    started_at: new Date().toISOString(),
    completed_at: new Date().toISOString(),
    error_message: null,
    progress: [
      {
        event_type: 'analysis.started',
        label: 'Analysis started',
        detail: 'Collecting repository and evidence context for the first pass.',
        created_at: new Date().toISOString(),
      },
      {
        event_type: 'analysis.completed',
        label: 'Assessment ready',
        detail: 'The operating snapshot is now available in this application hub.',
        created_at: new Date().toISOString(),
      },
    ],
    trace: {
      iterations: 2,
      duration_ms: 1420,
      tool_calls: [{ name: 'git_read_file' }, { name: 'datasource_list_assets' }],
    },
    plan_id: 'free',
    entitlement_snapshot: {
      plan_id: 'free',
      name: 'Free Check',
      monthly_vm_runs: 0,
      max_vm_minutes_per_run: 0,
      monthly_llm_calls: 5,
      max_llm_tier: 'concise',
      allowed_execution_profiles: [],
      recurring_checks_enabled: false,
    },
    scan_results: [
      {
        check_id: 'source.evidence',
        category: 'Repository structure',
        status: 'pass',
        severity: 'info',
        confidence: 0.9,
        title: 'Source evidence available',
        summary: 'Vhoop found managed source and Odoo Sale module evidence to ground the snapshot.',
        evidence_refs: [{ kind: 'data_asset', label: 'odoo-sale.zip:addons/sale/__manifest__.py', asset_id: 'da_sample_1', path: 'addons/sale/__manifest__.py' }],
        upgrade_relevance: 'Paid checks become more useful once Vhoop can execute against the repo.',
        plan_required: null,
      },
      {
        check_id: 'supply.manifests',
        category: 'Supply chain',
        status: 'pass',
        severity: 'medium',
        confidence: 0.8,
        title: 'Dependency manifest proof',
        summary: 'Uploaded evidence includes the Odoo module manifest and Python requirements.',
        evidence_refs: [{ kind: 'data_asset', label: 'odoo-sale.zip:addons/sale/__manifest__.py', asset_id: 'da_sample_1', path: 'addons/sale/__manifest__.py' }],
        upgrade_relevance: 'Run manifest validation to prove dependency posture.',
        plan_required: 'launch',
      },
      {
        check_id: 'quality.tests',
        category: 'AI-development quality',
        status: 'pass',
        severity: 'high',
        confidence: 0.85,
        title: 'Regression test evidence',
        summary: 'Odoo Sale regression tests are visible in the submitted evidence.',
        evidence_refs: [{ kind: 'data_asset', label: 'odoo-sale.zip:addons/sale/tests/test_sale_order.py', asset_id: 'da_sample_1', path: 'addons/sale/tests/test_sale_order.py' }],
        upgrade_relevance: 'Run the test suite to prove sales-order behavior.',
        plan_required: 'operate',
      },
      {
        check_id: 'execution.locked',
        category: 'Execution proof',
        status: 'locked',
        severity: 'info',
        confidence: 1,
        title: 'Code execution intentionally locked',
        summary: 'Free snapshots never execute customer code. Build, test, typecheck, and runtime probes require a paid proof plan.',
        evidence_refs: [{ kind: 'missing_artifact', label: 'No runtime proof was attempted' }],
        upgrade_relevance: 'Upgrade to run tests, builds, health probes, and recurring launch checks.',
        plan_required: 'launch',
      },
    ],
    execution_results: [
      {
        profile_id: 'test',
        profile_name: 'Test suite',
        status: 'blocked',
        command: [],
        exit_code: null,
        duration_ms: null,
        output_excerpt: '',
        artifacts: [],
        evidence_refs: [],
        plan_required: 'operate',
        summary: 'Free snapshots never execute customer code. Upgrade to run this profile in an isolated proof environment.',
      },
    ],
    tool_artifacts: [],
    evidence_refs: [],
    implementation_quality: {
      score: 62,
      label: 'Useful spine, but proof is thin',
      summary: "This score measures whether the app has enough proof for long-term AI-assisted development, not whether one model likes another model's output.",
      evidence_status: 'locked',
      signals: [
        {
          label: 'Tests and regression proof',
          status: 'pass',
          detail: 'Odoo Sale test evidence is visible; paid execution should run it to prove behavior.',
        },
        {
          label: 'Type and contract safety',
          status: 'warn',
          detail: 'Schemas and typechecks make generated code easier to evolve safely.',
        },
      ],
      locked_checks: [
        {
          profile_id: 'build',
          title: 'Build',
          plan_required: 'launch',
          reason: 'Runtime proof requires a paid isolated execution run.',
        },
        {
          profile_id: 'test',
          title: 'Test suite',
          plan_required: 'operate',
          reason: 'Runtime proof requires a paid isolated execution run.',
        },
      ],
      ai_development_signals: [
        {
          label: 'Safe iteration runway',
          status: 'needs-proof',
          detail: 'Passing paid execution profiles are the strongest signal that AI can keep improving the app without eroding trust.',
        },
      ],
    },
    score_breakdown: [
      {
        category: 'Source and repository evidence',
        weight: 8,
        earned: 6,
        max_score: 8,
        status: 'pass',
        summary: 'Managed source and parsed evidence are present.',
        check_ids: ['source.evidence', 'forge.repo'],
        evidence_refs: [],
      },
      {
        category: 'Build and reproducibility',
        weight: 12,
        earned: 9,
        max_score: 12,
        status: 'pass',
        summary: 'Odoo module manifests and deployment evidence make the app reproducible enough for a static free snapshot.',
        check_ids: ['build.reproducibility'],
        evidence_refs: [{ kind: 'data_asset', label: 'odoo-sale.zip:requirements.txt', asset_id: 'da_sample_1', path: 'requirements.txt' }],
      },
      {
        category: 'Dependency and supply-chain posture',
        weight: 10,
        earned: 8,
        max_score: 10,
        status: 'pass',
        summary: 'The Odoo module manifest and requirements evidence make dependency posture inspectable.',
        check_ids: ['supply.manifests'],
        evidence_refs: [{ kind: 'data_asset', label: 'odoo-sale.zip:addons/sale/__manifest__.py', asset_id: 'da_sample_1', path: 'addons/sale/__manifest__.py' }],
      },
      {
        category: 'Release and deployment readiness',
        weight: 15,
        earned: 12,
        max_score: 15,
        status: 'pass',
        summary: 'CI and deployment evidence are visible, but execution proof is still locked on the free plan.',
        check_ids: ['release.ci', 'deploy.path'],
        evidence_refs: [],
      },
      {
        category: 'Regression and AI-change safety',
        weight: 18,
        earned: 14,
        max_score: 18,
        status: 'pass',
        summary: 'Odoo Sale tests and model contract evidence make future AI-assisted changes safer, pending paid execution.',
        check_ids: ['quality.tests', 'contracts.validation'],
        evidence_refs: [{ kind: 'data_asset', label: 'odoo-sale.zip:addons/sale/tests/test_sale_order.py', asset_id: 'da_sample_1', path: 'addons/sale/tests/test_sale_order.py' }],
      },
      {
        category: 'Security, auth, and secret hygiene',
        weight: 15,
        earned: 10,
        max_score: 15,
        status: 'pass',
        summary: 'Environment and access-control evidence is present, with no secret-like value shown in the sample evidence.',
        check_ids: ['config.env', 'security.auth', 'security.secrets'],
        evidence_refs: [{ kind: 'data_asset', label: 'odoo-sale.zip:.env.example', asset_id: 'da_sample_1', path: '.env.example' }],
      },
      {
        category: 'Data and compliance posture',
        weight: 8,
        earned: 5,
        max_score: 8,
        status: 'warn',
        summary: 'Sales-order data is business-critical and needs audit, retention, and permission controls before production reliance.',
        check_ids: ['data.compliance'],
        evidence_refs: [{ kind: 'data_asset', label: 'odoo-sale.zip:addons/sale/models/sale_order.py', asset_id: 'da_sample_1', path: 'addons/sale/models/sale_order.py' }],
      },
      {
        category: 'Observability and operations',
        weight: 9,
        earned: 8,
        max_score: 9,
        status: 'pass',
        summary: 'Runbook evidence exists for ERP operational ownership, incidents, and releases.',
        check_ids: ['ops.monitoring', 'ops.runbooks'],
        evidence_refs: [{ kind: 'data_asset', label: 'odoo-sale.zip:docs/runbook.md', asset_id: 'da_sample_1', path: 'docs/runbook.md' }],
      },
      {
        category: 'Operational ownership and continuity',
        weight: 5,
        earned: 4,
        max_score: 5,
        status: 'pass',
        summary: 'Runbook evidence shows ownership and continuity intent, pending runtime verification.',
        check_ids: ['ops.runbooks'],
        evidence_refs: [{ kind: 'data_asset', label: 'odoo-sale.zip:docs/runbook.md', asset_id: 'da_sample_1', path: 'docs/runbook.md' }],
      },
    ],
    score_confidence: 'medium',
    evidence_level: 'static-evidence',
    raw_score: 76,
    confidence_cap: 78,
    critical_penalties: [],
    execution_provider: 'none',
    execution_policy: {
      free_code_execution_allowed: false,
      blocked_command_classes: ['dependency install', 'database migration apply', 'secret access', 'arbitrary shell'],
    },
    llm_usage: {
      plan_id: 'free',
      llm_calls_used_this_month: 1,
      monthly_llm_calls: 5,
      vm_runs_used_this_month: 0,
      monthly_vm_runs: 0,
      remaining_deep_reports: 0,
      recurring_checks_enabled: false,
    },
    vm_usage: {
      plan_id: 'free',
      vm_runs_used_this_month: 0,
      monthly_vm_runs: 0,
      llm_calls_used_this_month: 0,
      monthly_llm_calls: 5,
      remaining_deep_reports: 0,
      recurring_checks_enabled: false,
    },
  },
}
